import AsyncStorage from '@react-native-async-storage/async-storage';

const DRAFT_KEY = 'listing_draft';
const DRAFT_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface ListingDraft {
  title: string;
  description: string;
  price: string;
  size: string;
  category: string;
  condition: string;
  sportType?: string;
  clubId: string; // Single club selection
  savedAt: number;
}

export const draftStorage = {
  /**
   * Save a draft of the listing form data
   */
  async saveDraft(draft: Omit<ListingDraft, 'savedAt'>): Promise<void> {
    try {
      const draftWithTimestamp: ListingDraft = {
        ...draft,
        savedAt: Date.now(),
      };
      await AsyncStorage.setItem(DRAFT_KEY, JSON.stringify(draftWithTimestamp));
    } catch (error) {
      console.error('Failed to save draft:', error);
      // Silent failure - don't block user workflow
    }
  },

  /**
   * Get the saved draft if it exists and hasn't expired
   * Returns null if no draft exists or if it has expired
   */
  async getDraft(): Promise<ListingDraft | null> {
    try {
      const draftJson = await AsyncStorage.getItem(DRAFT_KEY);
      if (!draftJson) {
        return null;
      }

      const draft: ListingDraft = JSON.parse(draftJson);
      const now = Date.now();
      const isExpired = now - draft.savedAt > DRAFT_EXPIRY_MS;

      if (isExpired) {
        // Auto-delete expired draft
        await this.clearDraft();
        return null;
      }

      return draft;
    } catch (error) {
      console.error('Failed to get draft:', error);
      return null;
    }
  },

  /**
   * Clear the saved draft
   */
  async clearDraft(): Promise<void> {
    try {
      await AsyncStorage.removeItem(DRAFT_KEY);
    } catch (error) {
      console.error('Failed to clear draft:', error);
      // Silent failure
    }
  },

  /**
   * Check if a valid draft exists
   */
  async hasDraft(): Promise<boolean> {
    const draft = await this.getDraft();
    return draft !== null;
  },
};
